import moment from "moment";

import { type IAppProviderHandler } from "helpers/AppProvider.api.handler";
import {
    AccountFlags,
    AppDefaultInfo,
    type BrandingInfo,
    BuilderConfigFlags,
    type BuilderInfo,
    CurrencyLocale,
    DateLocale,
    GetBuilderFlags,
    type GlobalInfo,
    IntegrationBuilderProfile,
    IntegrationUserProfile,
    noOptInDecisions,
    SetUseAccountFlagsAsSourceDefault,
    type SupportContactInfo,
    TimeLocale,
} from "helpers/AppProvider.types";

import { EmptyResponseEntity } from "types/emptyResponseEntity";

import { APIHandler } from "utilities/apiHandler";
import { isNullOrUndefined } from "utilities/object/object";

export class AppProviderBTJScriptGlobalsHandler implements IAppProviderHandler {
    isLegacy = true;

    getSync(): GlobalInfo {
        const isBuilderInSession = !isNullOrUndefined(window.btJScriptGlobals.getBuilderId);

        let builderInfo: BuilderInfo | null = null;
        let supportContactInfo: SupportContactInfo | null = null;
        let brandingInfo: BrandingInfo | null = null;

        const appDefaultInfo = new AppDefaultInfo(window.btJScriptGlobals.appDefaultFlags);
        SetUseAccountFlagsAsSourceDefault(appDefaultInfo.useFeatureFlagBuilderPrefsForReads);

        if (isBuilderInSession) {
            const accountFlagsData = window.btJScriptGlobals.accountFlags as
                | Record<string, boolean>
                | undefined;
            builderInfo = {
                encryptedBuilderId: window.btJScriptGlobals.encryptedBuilderId,
                builderId: parseInt(window.btJScriptGlobals.getBuilderId),
                name: window.btJScriptGlobals.builderName,
                isDemoAccount: window.btJScriptGlobals.isDemoAccount,
                scheduleDefaultTabForCurrentPortal:
                    window.btJScriptGlobals.builderInformation.scheduleDefaultTab,
                selectionDefaultBuilderView: 0, // not supported in non-SPA
                activityCalendarDefaultView:
                    window.btJScriptGlobals.builderInformation.activityCalendarDefaultView,
                weekStartDay: window.btJScriptGlobals.getBuilderWeekStartDay,
                locale: {
                    currencyLocale: new CurrencyLocale({
                        currencySymbol: window.btJScriptGlobals.getBuilderCurrencySymbol,
                        decimalSeparator: window.btJScriptGlobals.getBuilderNumberDecimalSeparator,
                        groupSeparator: window.btJScriptGlobals.getBuilderNumberGroupSeparator,
                    }),
                    dateLocale: new DateLocale(
                        window.btJScriptGlobals.builderShortDateFormatForMoment,
                    ),
                    timeLocale: new TimeLocale({
                        format: window.btJScriptGlobals.builderTimeFormatForMoment,
                        is24HourClock: window.btJScriptGlobals.uses24HourClock,
                        timeZoneName: window.btJScriptGlobals.builderTimeZoneName,
                    }),
                },
                flags: GetBuilderFlags(
                    window.btJScriptGlobals.btAdminFlags as Record<string, boolean>,
                    accountFlagsData,
                ),
                accountFlags: new AccountFlags(accountFlagsData),
                configFlags: new BuilderConfigFlags(window.btJScriptGlobals.configFlags),
                taxesOptOut: window.btJScriptGlobals.taxesOptOut,
                setupDate: moment(window.btJScriptGlobals.setupDate),
                showAccountingBannerAd: window.btJScriptGlobals.showAccountingBannerAd,
                canUseExternalTemplates: window.btJScriptGlobals.canUseExternalTemplates,
                redirectToSetupExperience: window.btJScriptGlobals.redirectToSetupExperience,
                loginsDisabled: window.btJScriptGlobals.loginsDisabled,
                hasJobGroups: window.btJScriptGlobals.hasJobGroups,
                timeClockSettings: window.btJScriptGlobals.builderInformation.timeClockSettings,
                hasLegacySelectionsData: window.btJScriptGlobals.hasLegacySelectionsData,
                isFullySelectionsReimagined: window.btJScriptGlobals.isFullySelectionsReimagined,
                // Not emitted to the legacy global; its only reader is SPA-only, so false is safe.
                newJobsUseSelectionsReimagined: false,
            };

            supportContactInfo = {
                email: window.btJScriptGlobals.supportContactInfo.email,
                header: window.btJScriptGlobals.supportContactInfo.header,
                phoneNumber: window.btJScriptGlobals.supportContactInfo.phoneNumber,
                timezone: window.btJScriptGlobals.supportContactInfo.timezone,
                workDays: window.btJScriptGlobals.supportContactInfo.workDays,
                workHours: window.btJScriptGlobals.supportContactInfo.workHours,
            };
            brandingInfo = {
                primaryColor: `#${window.btJScriptGlobals.builderHeaderColor}`,
            };
        }
        return {
            environmentInfo: {
                isProduction: window.btJScriptGlobals.isProduction,
                name: window.btJScriptGlobals.environmentName,
                color: window.btJScriptGlobals.environmentColor,
                supportMobileEphemeralLogin:
                    window.btJScriptGlobals?.supportMobileEphemeralLogin ?? false,
                auth0Url: window.btJScriptGlobals.auth0Url,
                quartzPath: "",
                isPublicEph: false,
            },
            appDefaultInfo,
            thirdPartyInfo: {
                googleMapsApiKey: window.btJScriptGlobals.googleMapsApiKey,
                sentryCurrentEnvironmentReleaseId: window.btJScriptGlobals.sentryRelease,
                firebaseConfig: window.btJScriptGlobals.firebaseConfiguration ?? null,
                sardineClientId: window.btJScriptGlobals.sardineClientId ?? null,
                sardineSessionKey: window.btJScriptGlobals.sardineSessionKey ?? null,
            },
            userInfo: {
                loginType: window.btJScriptGlobals.loginTypeInt,
                credentialLoginType: window.btJScriptGlobals.credentialLoginTypeInt,
                timeoutMinutes: window.btJScriptGlobals.timeoutMinutes,
                isImpersonatingBuilder: window.btJScriptGlobals.isImpersonatingBuilder,
                userName: window.btJScriptGlobals.userIdDisplay,
                fullName: window.btJScriptGlobals.userDisplayName,
                email: window.btJScriptGlobals.userEmail,
                legacyEmailDoNotUse: window.btJScriptGlobals.userEmail,
                firstName: window.btJScriptGlobals.userFirstName,
                isAdmin: window.btJScriptGlobals.isAdmin,
                globalUserId:
                    window.btJScriptGlobals.globalUserId === 0
                        ? null
                        : window.btJScriptGlobals.globalUserId,
                // The numeric BT userId is only supplied by the SPA UserInfo API payload,
                // not the legacy webforms btJScriptGlobals, so it is null in this path.
                userId: null,
                isBuilderImpersonatingOwner: window.btJScriptGlobals.isBuilderImpersonatingOwner,
                orgLinks: [],
                systemAlerts: window.btJScriptGlobals.systemAlerts,
                supportChatSettings: null,
                roleInfo: window.btJScriptGlobals.roleInfo,
                shouldRedirectToSetupExperience:
                    window.btJScriptGlobals.shouldRedirectToSetupExperience,
                hasAcceptedTermsAndPolicy: window.btJScriptGlobals.hasAcceptedTermsAndPolicy,
                // Opt-ins are not serialized to btJScriptGlobals, so every feature reports
                // "never decided" here. Safe because the only consumer is the MainNavigation
                // toggle, and MainNavigation renders solely inside FullPageLayout /
                // ListPageLayout — both of which construct AppProvider with the APIX
                // AppProviderHandler and are never passed the legacy one. If a webforms page
                // ever renders MainNavigation, an opted-in user would show as opted out, so
                // serialize the real values here rather than reporting no decisions.
                optIns: noOptInDecisions,
                permissions: null, // not supported in non-SPA
            },
            integrationUserProfile: window.btJScriptGlobals.integrationUserProfile
                ? new IntegrationUserProfile(window.btJScriptGlobals.integrationUserProfile)
                : null,
            builderInfo,
            brandingInfo,
            integrationBuilderProfile: window.btJScriptGlobals.integrationBuilderProfile
                ? new IntegrationBuilderProfile(window.btJScriptGlobals.integrationBuilderProfile)
                : null,
            supportContactInfo,
        };
    }

    async get(): Promise<GlobalInfo> {
        return Promise.resolve(this.getSync());
    }

    async authLog(): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Login/LogAuth`, {
            method: "POST",
            responseType: EmptyResponseEntity,
        });
    }
}
